import { FileDto, FileType, FileUploadResponse, DocumentType } from "../../types/file.types";
import apiService from "../api/api.service";

class FileService {
    private static instance: FileService;

    private constructor() { }

    public static getInstance(): FileService {
        if (!FileService.instance) {
            FileService.instance = new FileService();
        }
        return FileService.instance;
    }

    public async uploadFile(file: File, type: FileType, name?: string, documentType?: DocumentType | null): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        if (name) {
            formData.append('name', name);
        }
        if (documentType) {
            formData.append('documentType', documentType);
        }

        const response = await apiService.post<FileUploadResponse>('/files/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    }

    public async uploadProfilePictureFile(file: File, name?: string, documentType?: DocumentType | null): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        if (name) {
            formData.append('name', name);
        }
        if (documentType) {
            formData.append('documentType', documentType);
        }

        const response = await apiService.post<FileUploadResponse>('/files/upload-profile-picture', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    }

    // Kişiye özel dosya yükleme metodu
    public async uploadFileForEmployee(
        file: File,
        employeeId: number,
        name: string,
        documentType: DocumentType,
        isVisible: boolean = true
    ): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('employeeId', employeeId.toString());
        formData.append('name', name);
        formData.append('documentType', documentType);
        formData.append('isVisible', isVisible.toString());

        const response = await apiService.post<FileUploadResponse>('/files/for-employee', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    }

    public async getFiles(): Promise<FileDto[]> {
        const response = await apiService.get<FileDto[]>('/files/company');
        return response;
    }

    public async getFile(id: number): Promise<FileDto> {
        const response = await apiService.get<FileDto>(`/files/${id}`);
        return response;
    }

    // Dosya güncelleme metodu
    public async updateFileForEmployee(
        fileId: number,
        name?: string,
        documentType?: DocumentType,
        isVisible?: boolean,
        file?: File
    ): Promise<FileUploadResponse> {
        const formData = new FormData();

        if (name) {
            formData.append('name', name);
        }
        if (documentType) {
            formData.append('documentType', documentType);
        }
        if (isVisible !== undefined) {
            formData.append('isVisible', isVisible.toString());
        }
        if (file) {
            formData.append('file', file);
        }

        const response = await apiService.put<FileUploadResponse>(`/files/by-admin/${fileId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    }

    // Dosya silme metodu
    public async deleteFile(id: number): Promise<void> {
        await apiService.delete(`/files/admin/${id}`);
    }

    // Dosya görünürlük durumunu değiştirme
    public async toggleFileVisibility(fileId: number, isVisible: boolean): Promise<void> {
        await apiService.patch(`/files/${fileId}/visibility`, { isVisible });
    }

    public async getFileByUserDocuments(fileType: FileType, userId: number): Promise<FileDto[]> {
        const response = await apiService.get<FileDto[]>(`/files/user/${userId}/type/${fileType}`);
        return response;
    }

    public async updateFileByUserDocuments(fileId: number, fileType: FileType, name?: string, file?: File, documentType?: DocumentType | null): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('type', fileType);
        formData.append('name', name || '');
        if (file) {
            formData.append('file', file);
        }
        if (documentType) {
            formData.append('documentType', documentType);
        }
        const response = await apiService.put<FileUploadResponse>(`/files/${fileId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    }

    // Dosya görüntüleme/indirme için binary data alma
    public async getFileViewUrl(fileName: string): Promise<Blob> {
        // Blob için doğrudan fetch kullanıyoruz (apiService wrapper'ı blob için uygun değil)
        const token = localStorage.getItem('token');
        let baseURL = process.env.REACT_APP_API_URL || 'http://localhost:7575/api';
        
        // baseURL sonunda / yoksa ekle
        if (!baseURL.endsWith('/')) {
            baseURL += '/';
        }
        
        const url = `${baseURL}files/secure?fileName=${encodeURIComponent(fileName)}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        
        // Response kontrolü
        if (!response.ok) {
            const errorText = await response.text();
            // console.error('❌ File fetch error:', errorText);
            throw new Error(`Dosya yüklenemedi`);
        }

        const blob = await response.blob();        
        return blob;
    }

    // Dosyayı blob olarak görüntüle (yeni sekme)
    public async viewFileAsBlob(fileName: string): Promise<void> {
        const blob = await this.getFileViewUrl(fileName);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Memory leak'i önlemek için 100ms sonra temizle
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 100);
    }

    // Dosyayı blob olarak indir
    public async downloadFileAsBlob(fileName: string, downloadName: string): Promise<void> {
        const blob = await this.getFileViewUrl(fileName);
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(url);
    }

}
export default FileService;
